import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Badge, Button, Spinner } from "reactstrap";

import { getClientTags, syncClientTags } from "../../services/clientTagService";
import { ClientTag, ClientTagChip } from "../../types/clientTag";

type ClientTagsEditorProps = {
  clientId: number;
  initialTags?: ClientTagChip[] | null;
  onChanged?: (tags: ClientTagChip[]) => void;
  disabled?: boolean;
};

const ClientTagsEditor = ({
  clientId,
  initialTags = [],
  onChanged,
  disabled = false,
}: ClientTagsEditorProps) => {
  const { t } = useTranslation();
  const [catalog, setCatalog] = useState<ClientTag[]>([]);
  const [selectedIds, setSelectedIds] = useState<number[]>(
    () => (initialTags ?? []).map((tag) => tag.id)
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setSelectedIds((initialTags ?? []).map((tag) => tag.id));
  }, [initialTags]);

  const loadCatalog = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getClientTags({ is_active: true, per_page: 100 });
      const active = response.data;
      const assignedInactive = (initialTags ?? []).filter(
        (tag) => !tag.is_active && !active.some((item) => item.id === tag.id)
      );
      setCatalog([
        ...active,
        ...assignedInactive.map((tag) => ({
          id: tag.id,
          tenant_id: 0,
          name: tag.name,
          color: tag.color,
          is_active: tag.is_active,
          created_at: "",
          updated_at: "",
        })),
      ]);
    } catch (error: any) {
      toast.error(error?.data || t("clients.tags.toast.loadError"));
    } finally {
      setLoading(false);
    }
  }, [initialTags, t]);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const toggleTag = async (tagId: number) => {
    if (disabled || saving) {
      return;
    }

    const next = selectedSet.has(tagId)
      ? selectedIds.filter((id) => id !== tagId)
      : [...selectedIds, tagId];

    setSaving(true);
    setSelectedIds(next);
    try {
      const response = await syncClientTags(clientId, next);
      const tags = response.data.tags ?? [];
      setSelectedIds(tags.map((tag) => tag.id));
      onChanged?.(tags);
      toast.success(response.message || t("clients.tags.toast.synced"));
    } catch (error: any) {
      setSelectedIds(selectedIds);
      toast.error(error?.data || t("clients.tags.toast.syncError"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex align-items-center gap-2 text-muted">
        <Spinner size="sm" />
        <span>{t("clients.tags.loading")}</span>
      </div>
    );
  }

  if (catalog.length === 0) {
    return (
      <div className="text-muted small">
        <p className="mb-2">{t("clients.tags.emptyCatalog")}</p>
        <Button
          color="soft-primary"
          size="sm"
          tag="a"
          href="/settings/client-tags"
        >
          {t("clients.tags.manageCatalog")}
        </Button>
      </div>
    );
  }

  return (
    <div className="d-flex flex-wrap gap-2 align-items-center">
      {catalog.map((tag) => {
        const selected = selectedSet.has(tag.id);
        const style =
          selected && tag.color && /^#[0-9A-Fa-f]{6}$/.test(tag.color)
            ? {
                backgroundColor: tag.color,
                borderColor: tag.color,
                color: "#fff",
              }
            : undefined;

        return (
          <Button
            key={tag.id}
            type="button"
            size="sm"
            color={selected ? "primary" : "light"}
            outline={!selected}
            disabled={disabled || saving || (!tag.is_active && !selected)}
            style={style}
            onClick={() => toggleTag(tag.id)}
            className="d-inline-flex align-items-center gap-1"
          >
            {tag.name}
            {!tag.is_active ? (
              <Badge color="secondary" className="fs-11">
                {t("clients.tags.inactive")}
              </Badge>
            ) : null}
          </Button>
        );
      })}
      {saving ? <Spinner size="sm" /> : null}
    </div>
  );
};

export default ClientTagsEditor;
